import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ServerInferRequest } from "@ts-rest/core";
import { accountingIntegrationContract } from "@common/contracts/accounting-integration.contract";
import {
    BulkUpdateImportedAccountsBody,
    ImportedAccount,
} from "@common/schemas/domains/accounting/importedAccount.schema";
import { ImportedAccountGroup } from "@common/schemas/domains/accounting/importedAccountGroup.schema";
import {
    accountingMigrationSchema,
    CreateImportedAccountsFromBalanceSheetBody,
    CreateImportedAccountsFromCoaBody,
} from "@common/schemas/accounting-integration.schema";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useScope } from "@state/scopeContext.lib";
import {
    AccountingMigrationExternalSystem,
    MigrationObjectType,
} from "@common/types/domains/accounting/accountingMigration";
import { isJeConversionInProgress } from "@common/helpers/jeConversionInProgress";
import { invalidateAccountRelatedQueries } from "./account.api";
import { tasksQueryKeys } from "./task.api";
import {
    accountingIntegrationContractClient,
    accountingMigrationQueryKeys,
} from "./accountingIntegration.shared";

export {
    DocumentMigrationError,
    documentMigrationErrorToastMessage,
    MIGRATION_EXTRACTION_FAILED_TOAST,
    MIGRATION_IMPORT_FAILED_TOAST,
    extractMigrationFileById,
    setGlExtractionBasis,
    useSetQbdFileMigrationUploadSourceMutation,
    useUpdateMigrationStartDateMutation,
    useUpdateMigrationEndDateMutation,
    useSubmitDocumentMigrationMutation,
    useSubmitQbdJsonDocumentMigrationMutation,
    useValidateDocumentMigration,
} from "./documentMigration.api";
export type { SubmitDocumentMigrationParams } from "./documentMigration.api";

type ImportedAccountsQueryFilters = ServerInferRequest<
    typeof accountingIntegrationContract.getAccounts
>["query"];

const importedAccountsQueryKeys = {
    all: ["imported-accounts"] as const,
    byEntity: (entityId: number) =>
        [...importedAccountsQueryKeys.all, entityId] as const,
    byEntityWithFilters: (
        entityId: number,
        filters?: ImportedAccountsQueryFilters,
    ) =>
        [
            ...importedAccountsQueryKeys.byEntity(entityId),
            filters ?? {},
        ] as const,
};

export function useAccountingMigration(
    workspaceId: string | null,
    entityId: number | undefined,
    options?: {
        refetchUntilMappingFinished?: boolean;
        refetchUntilConversionFinished?: boolean;
    },
) {
    return useQuery({
        queryKey: accountingMigrationQueryKeys.byEntity(entityId),
        queryFn: async () => {
            if (!workspaceId || !entityId) {
                return null;
            }

            try {
                const response =
                    await accountingIntegrationContractClient.getAccountingMigration(
                        {
                            params: {
                                workspaceId,
                                entityId,
                            },
                            extraHeaders: {
                                [SUPPRESS_ERROR_TOAST_HEADER]: "true",
                            },
                        },
                    );

                return accountingMigrationSchema.parse(
                    response.body.accountingMigration,
                );
            } catch (error) {
                if (isAxiosError(error) && error.response?.status === 404) {
                    return null;
                }
                throw error;
            }
        },
        enabled: !!workspaceId && !!entityId,
        refetchInterval: (query) => {
            if (options?.refetchUntilMappingFinished === true) {
                return query.state.data?.accountsMappedAt == null
                    ? 5000
                    : false;
            }

            if (options?.refetchUntilConversionFinished === true) {
                const accountingMigration = query.state.data;
                if (!accountingMigration) {
                    return false;
                }
                return isJeConversionInProgress(accountingMigration)
                    ? 5000
                    : false;
            }

            return false;
        },
    });
}

export function useWorkspaceAccountingMigrations(workspaceId: string | null) {
    return useQuery({
        queryKey: accountingMigrationQueryKeys.byWorkspace(workspaceId),
        queryFn: async () => {
            if (!workspaceId) {
                return [];
            }

            const response =
                await accountingIntegrationContractClient.listWorkspaceAccountingMigrations(
                    {
                        params: { workspaceId },
                    },
                );

            return response.body.accountingMigrations.map(
                (accountingMigration) =>
                    accountingMigrationSchema.parse(accountingMigration),
            );
        },
        enabled: !!workspaceId,
    });
}

export interface ImportedAccountsData {
    accounts: ImportedAccount[];
    accountGroups: ImportedAccountGroup[];
}

export function useImportedAccounts(
    workspaceId: string | null,
    entityId: number | undefined,
    filters?: ImportedAccountsQueryFilters,
) {
    return useQuery({
        queryKey: importedAccountsQueryKeys.byEntityWithFilters(
            entityId!,
            filters,
        ),
        queryFn: async (): Promise<ImportedAccountsData> => {
            const response =
                await accountingIntegrationContractClient.getAccounts({
                    params: {
                        workspaceId: workspaceId!,
                        entityId: entityId!,
                    },
                    query: {
                        requiresFinancialAccountMapping:
                            filters?.requiresFinancialAccountMapping != null
                                ? (`${filters.requiresFinancialAccountMapping}` as
                                      | "true"
                                      | "false")
                                : undefined,
                        orderBy: filters?.orderBy,
                    },
                });

            return {
                accounts: response.body.accounts,
                accountGroups: response.body.accountGroups,
            };
        },
        enabled: !!entityId && !!workspaceId,
    });
}

export function useBulkUpdateImportedAccounts(
    workspaceId: string,
    entityId: number,
) {
    const queryClient = useQueryClient();
    const { isViewOnly } = useViewOnlyRefusal();

    return useMutation({
        mutationFn: async (body: BulkUpdateImportedAccountsBody) => {
            const response =
                await accountingIntegrationContractClient.bulkUpdateImportedAccounts(
                    {
                        params: {
                            workspaceId,
                            entityId,
                        },
                        body,
                    },
                );

            return response.body.accounts;
        },
        onMutate: async (body) => {
            if (isViewOnly) {
                return;
            }
            const queryKey = importedAccountsQueryKeys.byEntity(entityId);

            await queryClient.cancelQueries({ queryKey });

            const previousQueries =
                queryClient.getQueriesData<ImportedAccountsData>({
                    queryKey,
                });

            const updatesById = new Map(
                body.updates.map(({ importedAccountId, ...accountUpdate }) => [
                    importedAccountId,
                    accountUpdate,
                ]),
            );

            queryClient.setQueriesData<ImportedAccountsData>(
                { queryKey },
                (old) => {
                    if (!old) {
                        return old;
                    }
                    return {
                        accounts: old.accounts.map((account) => {
                            const update = updatesById.get(account.id);
                            if (!update) {
                                return account;
                            }
                            const shouldResetSubtype =
                                update.kickType !== undefined &&
                                (update.kickType !== account.kickType ||
                                    (update.createNewAccount === true &&
                                        !account.createNewAccount));
                            return {
                                ...account,
                                ...update,
                                kickSubtype: shouldResetSubtype
                                    ? null
                                    : account.kickSubtype,
                                ...(update.accountId !== undefined
                                    ? { pendingAccountId: update.accountId }
                                    : {}),
                            };
                        }),
                        accountGroups: old.accountGroups,
                    };
                },
            );

            return { previousQueries };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousQueries) {
                for (const [key, data] of context.previousQueries) {
                    queryClient.setQueryData(key, data);
                }
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: importedAccountsQueryKeys.byEntity(entityId),
            });
        },
    });
}

interface CreateAccountingMigrationParams {
    startDate?: string;
    integrationConnectionId?: string;
    externalSystem: AccountingMigrationExternalSystem | null;
    syncChartOfAccounts: boolean;
    syncGeneralLedger: boolean;
    syncCounterparties: boolean;
    syncAttachments: boolean;
    syncClasses: boolean;
    syncCatalogItems: boolean;
    syncInvoices: boolean;
    syncBills: boolean;
}

export function useCreateAccountingMigrationMutation(
    workspaceId: string,
    entityId: number,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateAccountingMigrationParams) => {
            const response =
                await accountingIntegrationContractClient.createAccountingMigration(
                    {
                        params: {
                            workspaceId,
                            entityId,
                        },
                        body: {
                            startDate: params.startDate,
                            integrationConnectionId:
                                params.integrationConnectionId,
                            externalSystem: params.externalSystem,
                            syncChartOfAccounts: params.syncChartOfAccounts,
                            syncGeneralLedger: params.syncGeneralLedger,
                            syncCounterparties: params.syncCounterparties,
                            syncAttachments: params.syncAttachments,
                            syncClasses: params.syncClasses,
                            syncCatalogItems: params.syncCatalogItems,
                            syncInvoices: params.syncInvoices,
                            syncBills: params.syncBills,
                        },
                    },
                );

            return accountingMigrationSchema.parse(
                response.body.accountingMigration,
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byEntity(entityId),
            });
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byWorkspace(workspaceId),
            });
        },
    });
}

export function useRemoveAccountingMigrationMutation(
    workspaceId: string,
    entityId: number,
) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();

    return useMutation({
        mutationFn: async (params?: { entityId?: number }) => {
            const mutationEntityId = params?.entityId ?? entityId;
            await accountingIntegrationContractClient.removeAccountingMigration(
                {
                    params: { workspaceId, entityId: mutationEntityId },
                    body: {},
                    extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                },
            );

            return { entityId: mutationEntityId };
        },
        onSuccess: ({ entityId: removedEntityId }) => {
            queryClient.invalidateQueries({
                queryKey:
                    accountingMigrationQueryKeys.byEntity(removedEntityId),
            });
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byWorkspace(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: importedAccountsQueryKeys.byEntity(removedEntityId),
            });
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
            });
            queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
            });
            invalidateAccountRelatedQueries({
                entityIds: [removedEntityId],
                workspaceId,
            });
        },
    });
}

export function useRefreshAccountingMigrationMutation(workspaceId: string) {
    const queryClient = useQueryClient();
    const { activeWorkspaceKey } = useScope();

    return useMutation({
        mutationFn: async ({
            entityId,
            objectTypes,
        }: {
            entityId: number;
            objectTypes?: MigrationObjectType[];
        }) => {
            await accountingIntegrationContractClient.refreshAccountingMigration(
                {
                    params: { workspaceId, entityId },
                    body: { objectTypes },
                },
            );

            return { entityId };
        },
        onSuccess: ({ entityId: refreshedEntityId }) => {
            queryClient.invalidateQueries({
                queryKey:
                    accountingMigrationQueryKeys.byEntity(refreshedEntityId),
            });
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byWorkspace(workspaceId),
            });
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
            });
            queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
            });
        },
    });
}

export function useFinalizeAccountsMapping(entityId: number) {
    const queryClient = useQueryClient();
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useMutation({
        mutationFn: async ({
            isRefinalization,
        }: {
            isRefinalization?: boolean;
        }) => {
            await accountingIntegrationContractClient.finalizeAccountsMapping({
                params: {
                    workspaceId: workspaceId ?? "",
                    entityId,
                },
                body: { isRefinalization },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byEntity(entityId),
            });
            queryClient.invalidateQueries({
                queryKey: importedAccountsQueryKeys.byEntity(entityId),
            });
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
            });
            queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
            });
            invalidateAccountRelatedQueries({
                entityIds: [entityId],
                workspaceId,
            });
        },
    });
}

function useCreateImportedAccountsMutation<TBody, TData>(
    entityId: number,
    mutationFn: (body: TBody) => Promise<TData>,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byEntity(entityId),
            });
            queryClient.invalidateQueries({
                queryKey: importedAccountsQueryKeys.byEntity(entityId),
            });
        },
    });
}

export function useCreateImportedAccountsFromBalanceSheet(
    workspaceId: string,
    entityId: number,
) {
    return useCreateImportedAccountsMutation(
        entityId,
        async (body: CreateImportedAccountsFromBalanceSheetBody) => {
            const response =
                await accountingIntegrationContractClient.createImportedAccountsFromBalanceSheet(
                    {
                        params: { workspaceId, entityId },
                        body,
                    },
                );

            return accountingMigrationSchema.parse(
                response.body.accountingMigration,
            );
        },
    );
}

export function useCreateImportedAccountsFromCoa(
    workspaceId: string,
    entityId: number,
) {
    return useCreateImportedAccountsMutation(
        entityId,
        async (body: CreateImportedAccountsFromCoaBody) => {
            const response =
                await accountingIntegrationContractClient.createImportedAccountsFromCoa(
                    {
                        params: { workspaceId, entityId },
                        body,
                    },
                );

            return accountingMigrationSchema.parse(
                response.body.accountingMigration,
            );
        },
    );
}

export function useFinalizeMigratedDataMapping(
    entityId: number | undefined,
    onSuccess?: () => void,
) {
    const queryClient = useQueryClient();
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useMutation({
        mutationFn: async () => {
            if (!entityId) {
                throw new Error("Entity ID is required");
            }
            await accountingIntegrationContractClient.finalizeMigratedDataMapping(
                {
                    params: {
                        workspaceId: workspaceId ?? "",
                        entityId,
                    },
                    body: {},
                },
            );
        },
        onSuccess: () => {
            if (!entityId) {
                return;
            }
            onSuccess?.();
            queryClient.invalidateQueries({
                queryKey: accountingMigrationQueryKeys.byEntity(entityId),
            });
            queryClient.invalidateQueries({
                queryKey: importedAccountsQueryKeys.byEntity(entityId),
            });
            queryClient.invalidateQueries({
                queryKey: tasksQueryKeys.listByWorkspaceKey(activeWorkspaceKey),
            });
            queryClient.invalidateQueries({
                queryKey:
                    tasksQueryKeys.detailByWorkspaceKey(activeWorkspaceKey),
            });
            invalidateAccountRelatedQueries({
                entityIds: [entityId],
                workspaceId,
            });
        },
    });
}
